/** Pure helpers for product analytics (no Firebase). */

export const ELIGIBLE_ROLES = ['stylist', 'manager', 'beauty_manager', 'company_admin'];

export const PAGE_KEYS = ['/', '/ai', '/goals', '/customers', '/admin', '/settings'];

/** Seeded walkthrough accounts, not real staff. Mirrors DEMO_EMAIL_DOMAINS in app_usage_digest.py. */
export const DEMO_EMAIL_DOMAINS = ['linkup-demo.jp'];

/** GA4 `company_id` for demo accounts, so they can be split from real `link_up` users. */
export const DEMO_COMPANY_ID = 'link_up_demo';

export function isDemoEmail(email) {
  const at = typeof email === 'string' ? email.lastIndexOf('@') : -1;
  return at > 0 && DEMO_EMAIL_DOMAINS.includes(email.slice(at + 1).toLowerCase());
}

/**
 * D2: never send events for `dev` (KUROCO) or a missing role.
 * Demo accounts DO send (2026-10-16): they are the only way to test tracking without
 * logging in as real staff. They are tagged `company_id = link_up_demo` instead, so
 * GA4 reports filter them with 企業 != link_up_demo.
 */
export function shouldTrack(role, _email) {
  return Boolean(role) && role !== 'dev' && ELIGIBLE_ROLES.includes(role);
}

/**
 * GA4 user properties (user-scoped custom dimensions `company_id` / `role` / `app_user_id`).
 * Strings only, never email. Missing company becomes '(none)' so the dimension
 * still groups those users instead of dropping them.
 * `app_user_id` duplicates the built-in `user_id`: GA4 refuses to register `user_id`
 * as a custom dimension and the Data API has no dimension for it, so the analyst
 * cannot slice by person without this copy. The name `uid` is also refused by GA4
 * (it is the wire field for user_id), hence `app_user_id`. Omitted when no uid.
 */
export function userPropertiesFor({ uid, role, companyId, email }) {
  if (!shouldTrack(role, email)) return null;
  const company = typeof companyId === 'string' && companyId.trim() ? companyId.trim() : '(none)';
  const props = {
    company_id: isDemoEmail(email) ? DEMO_COMPANY_ID : company,
    role,
  };
  if (typeof uid === 'string' && uid.trim()) props.app_user_id = uid.trim();
  return props;
}

/** Collapse SPA paths to the six Slack rows. */
export function normalizePagePath(pathname) {
  const raw = typeof pathname === 'string' ? pathname : '';
  const path = raw.split('?')[0] || '/';
  if (path === '/' || path === '') return '/';
  if (path === '/admin/iam' || path.startsWith('/settings')) return '/settings';
  if (path === '/admin' || path.startsWith('/admin/')) return '/admin';
  if (path === '/ai' || path.startsWith('/ai/')) return '/ai';
  if (path === '/goals' || path.startsWith('/goals/')) return '/goals';
  if (path === '/customers' || path.startsWith('/customers/')) return '/customers';
  return null;
}
